<template>
  <div
    class="group rounded border px-4 py-3 text-sm transition-colors relative"
    :class="expanded
      ? 'border-teal-500 bg-teal-50'
      : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'"
  >
    <!-- Status dot + label + actions row -->
    <div class="flex items-start gap-2" @click="toggle">
      <span
        class="mt-1.5 w-2 h-2 rounded-full shrink-0"
        :class="{
          'bg-teal-500': idea.status === 'fresh',
          'bg-amber-500': idea.status === 'used',
          'bg-gray-400': idea.status === 'archived',
        }"
      ></span>

      <div class="flex-1 min-w-0 cursor-pointer">
        <div class="font-medium text-gray-900 truncate">{{ idea.display_label }}</div>
        <div class="flex items-center gap-2 mt-0.5">
          <span class="text-xs text-gray-400">{{ timeAgo(idea.created_at) }}</span>
          <span
            v-if="idea.usage_count > 0"
            class="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700"
          >{{ idea.usage_count }} use{{ idea.usage_count === 1 ? '' : 's' }}</span>
        </div>
      </div>

      <!-- Actions (visible on hover / when expanded) -->
      <div class="flex items-center gap-1 shrink-0" :class="expanded ? '' : 'opacity-0 group-hover:opacity-100'" @click.stop>
        <button
          v-if="idea.status !== 'archived'"
          @click="$emit('archive', idea.id)"
          class="p-1 rounded text-gray-400 hover:text-amber-600 hover:bg-amber-50"
          title="Archive"
        >
          <Archive :size="14" />
        </button>
        <button
          @click="confirmDelete"
          class="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
          title="Delete"
        >
          <Trash2 :size="14" />
        </button>
      </div>
    </div>

    <!-- Expanded: editable body -->
    <div v-if="expanded" class="mt-3" @click.stop>
      <textarea
        v-model="editBody"
        class="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800
               focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none resize-y"
        rows="4"
        @keydown.meta.enter.prevent="saveEdit"
        @keydown.ctrl.enter.prevent="saveEdit"
      ></textarea>
      <div class="flex items-center gap-2 mt-2">
        <button
          @click="saveEdit"
          :disabled="!editBody.trim() || editBody === idea.body"
          class="text-xs font-medium px-3 py-1.5 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
        >Save</button>
        <button
          @click="expanded = false"
          class="text-xs font-medium px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
        >Cancel</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { Archive, Trash2 } from 'lucide-vue-next'

const props = defineProps({
  idea: Object,
})

const emit = defineEmits(['update', 'delete', 'archive'])

const expanded = ref(false)
const editBody = ref(props.idea.body)

watch(() => props.idea.body, (v) => { editBody.value = v })

function toggle() {
  expanded.value = !expanded.value
  if (expanded.value) editBody.value = props.idea.body
}

function saveEdit() {
  const body = editBody.value.trim()
  if (!body || body === props.idea.body) return
  emit('update', props.idea.id, { body })
  expanded.value = false
}

function confirmDelete() {
  if (confirm('Delete this idea? This cannot be undone.')) {
    emit('delete', props.idea.id)
  }
}

function timeAgo(dateStr) {
  if (!dateStr) return ''
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 30) return `${diffDays}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
</script>
